import { useState } from "react";

// Plain DMCA / takedown form. Mailto for v1 — when volume warrants, swap to a
// real submission endpoint that writes to a `takedowns` table for ops review.

export default function Dmca(): JSX.Element {
  const [streamerUrl, setStreamerUrl] = useState("");
  const [characterUrl, setCharacterUrl] = useState("");
  const [ownership, setOwnership] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");

  function buildMailto(): string {
    const body = [
      "DMCA / Content Takedown Request",
      "",
      `Streamer wall URL: ${streamerUrl}`,
      `Character / supporter URL: ${characterUrl}`,
      "",
      "Statement of ownership / authority to act:",
      ownership,
      "",
      `Reporter name: ${reporterName}`,
      `Reporter email: ${reporterEmail}`,
      "",
      "I have a good faith belief that the use of the material described above is not authorized by the copyright owner, its agent, or the law.",
      "I swear, under penalty of perjury, that the information above is accurate and that I am the copyright owner or am authorized to act on behalf of the owner.",
    ].join("\n");
    return `mailto:dmca@subswall.com?subject=${encodeURIComponent("Takedown Request")}&body=${encodeURIComponent(body)}`;
  }

  return (
    <div className="legal-page" style={{ maxWidth: 720, margin: "40px auto", padding: "0 24px", color: "#eee" }}>
      <h1 style={{ fontFamily: "Tomorrow, sans-serif", fontWeight: 800 }}>
        Copyright takedown (DMCA)
      </h1>
      <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
        SubsWall honors valid copyright takedown notices under the Digital Millennium
        Copyright Act (DMCA) and equivalent international laws (EU Directive 2019/790).
        If you believe content on SubsWall (a streamer's wall, a donor's character, an
        uploaded message) infringes your copyright or right of publicity, submit a
        notice using the form below or email{" "}
        <a href="mailto:dmca@subswall.com" style={{ color: "#ffb400" }}>dmca@subswall.com</a>.
      </p>

      <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
        Response SLA: <strong>48 hours</strong>. Flagged content is removed from public
        wall renderings pending review.
      </p>

      <h2 style={{ marginTop: 32 }}>Submit a takedown</h2>
      <form onSubmit={(e) => { e.preventDefault(); window.location.href = buildMailto(); }} style={{ display: "grid", gap: 14 }}>
        <label style={{ display: "block" }}>
          <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 12 }}>Streamer wall URL (subswall.com/handle)</div>
          <input
            value={streamerUrl}
            onChange={(e) => setStreamerUrl(e.target.value)}
            required
            placeholder="https://subswall.com/streamer_name"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block" }}>
          <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 12 }}>Character / supporter URL (subswall.com/s/slug)</div>
          <input
            value={characterUrl}
            onChange={(e) => setCharacterUrl(e.target.value)}
            required
            placeholder="https://subswall.com/s/u-someone"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block" }}>
          <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 12 }}>Statement of ownership or authority</div>
          <textarea
            value={ownership}
            onChange={(e) => setOwnership(e.target.value)}
            required
            rows={4}
            placeholder="Describe the copyrighted work and your relationship to it (owner / authorized agent)"
            style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <label>
            <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 12 }}>Your name</div>
            <input
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label>
            <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 12 }}>Email</div>
            <input
              type="email"
              value={reporterEmail}
              onChange={(e) => setReporterEmail(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
        </div>

        <button
          type="submit"
          style={{
            background: "#ffb400",
            color: "#2d1b00",
            border: 0,
            padding: "12px 18px",
            borderRadius: 6,
            fontWeight: 800,
            fontFamily: "Tomorrow, sans-serif",
            letterSpacing: 0.5,
            cursor: "pointer",
            marginTop: 8,
          }}
        >
          OPEN EMAIL TO SEND NOTICE
        </button>

        <p style={{ opacity: 0.6, fontSize: 12, marginTop: 4 }}>
          Submitting opens your email client with a pre-filled message to dmca@subswall.com.
          Sending the email finalizes the notice.
        </p>
      </form>

      <h2 style={{ marginTop: 40 }}>What we do on receipt</h2>
      <ol style={{ opacity: 0.85, lineHeight: 1.7 }}>
        <li>We flag the reported content within 48 hours and remove it from public renderings while we review.</li>
        <li>We notify the donor / streamer whose content was reported and give them 7 days to file a counter-notice.</li>
        <li>If no counter-notice, the content is removed permanently. If a counter-notice is received, we restore the content and forward both notices to the parties to resolve directly.</li>
        <li>Repeat infringers are banned from the platform per our <a href="/terms" style={{ color: "#ffb400" }}>Terms of Service</a>.</li>
      </ol>

      <h2 style={{ marginTop: 32 }}>Counter-notice</h2>
      <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
        If you are a donor or streamer whose content was removed and you believe the
        removal was a mistake or misidentification, send a counter-notice to{" "}
        <a href="mailto:dmca@subswall.com" style={{ color: "#ffb400" }}>dmca@subswall.com</a>{" "}
        including a statement under penalty of perjury that you have a good faith belief the
        content was removed in error, your contact information, and consent to the
        jurisdiction of the United States District Court for the District of Wyoming.
      </p>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 6,
  color: "#eee",
  fontSize: 14,
  fontFamily: "inherit",
};
